/**
 * Site History tab (TECH-JOB-SCREEN §3e) — everything on file for this
 * job's site, grouped per device: past test reports, filled WOs, and
 * device photos from prior visits, so a tech is never guessing which
 * assembly is which or what happened last year.
 *
 * Data comes from GET /api/field/jobs/:jobId/site-history — first page
 * (20 items) per group up front, further pages fetched per group on
 * "Show more". Thumbnails are the server's cached ~400px variant
 * (?variant=thumb) and lazy-load; nothing beyond visible thumbnails is
 * preloaded (old iPads, weak signal). Documents open in the existing
 * PDF preview dialog; photos open the touch lightbox.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, attachmentDownloadUrl } from "@/lib/api";
import { AttachmentImage } from "@/components/attachment-image";
import { AttachmentPreviewDialog } from "@/components/attachment-preview-dialog";
import { PhotoLightbox } from "@/components/photo-lightbox";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { Building2, ChevronDown, FileText, Loader2, Wrench } from "lucide-react";
import { toast } from "sonner";

interface SiteHistoryItem {
    id: string;
    kind: "photo" | "document";
    name: string;
    date: string;
    assetId: string | null;
    thumbUrl?: string;
    url: string;
}

interface SiteHistoryGroup {
    assetId: string | null;
    label: string;
    totalCount: number;
    items: SiteHistoryItem[];
    nextCursor: string | null;
}

interface SiteHistoryResponse {
    siteId: string | null;
    groups: SiteHistoryGroup[];
}

function formatItemDate(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/** One collapsible device (or "Site documents") group. */
function HistoryGroup({
    jobId,
    group,
    defaultOpen,
    onOpenPhoto,
    onOpenDocument,
}: {
    jobId: string;
    group: SiteHistoryGroup;
    defaultOpen: boolean;
    onOpenPhoto: (photos: SiteHistoryItem[], index: number, groupLabel: string) => void;
    onOpenDocument: (item: SiteHistoryItem) => void;
}) {
    const [open, setOpen] = useState(defaultOpen);
    // Pages loaded past the first are appended locally; the initial page
    // lives in the parent query so a tab remount stays cheap.
    const [extraItems, setExtraItems] = useState<SiteHistoryItem[]>([]);
    const [nextCursor, setNextCursor] = useState<string | null>(group.nextCursor);
    const [loadingMore, setLoadingMore] = useState(false);

    const items = [...group.items, ...extraItems];
    const photos = items.filter((i) => i.kind === "photo");
    const documents = items.filter((i) => i.kind === "document");

    const loadMore = async () => {
        if (!nextCursor || loadingMore) return;
        setLoadingMore(true);
        try {
            const groupKey = group.assetId ?? "site";
            const res = await api.get<SiteHistoryResponse>(
                `/api/field/jobs/${jobId}/site-history?group=${encodeURIComponent(groupKey)}&cursor=${encodeURIComponent(nextCursor)}`,
            );
            const page = res.groups[0];
            setExtraItems((prev) => [...prev, ...(page?.items ?? [])]);
            setNextCursor(page?.nextCursor ?? null);
        } catch (e) {
            toast.error(e instanceof Error ? e.message : "Failed to load more history");
        } finally {
            setLoadingMore(false);
        }
    };

    const Icon = group.assetId ? Wrench : Building2;

    return (
        <Card className="bg-card">
            <CardContent className="p-0">
                <button
                    type="button"
                    onClick={() => setOpen((o) => !o)}
                    className="flex w-full items-center gap-2 px-4 py-3 text-left"
                >
                    <Icon className="h-4 w-4 shrink-0 text-primary" />
                    <span className="min-w-0 flex-1 truncate text-sm font-semibold">{group.label}</span>
                    <Badge variant="secondary" className="shrink-0 text-xs">
                        {group.totalCount} {group.totalCount === 1 ? "item" : "items"}
                    </Badge>
                    <ChevronDown
                        className={cn(
                            "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
                            open && "rotate-180",
                        )}
                    />
                </button>

                {open && (
                    <div className="space-y-3 px-4 pb-4">
                        {photos.length > 0 && (
                            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                                {photos.map((photo, idx) => (
                                    <button
                                        key={photo.id}
                                        type="button"
                                        onClick={() => onOpenPhoto(photos, idx, group.label)}
                                        className="relative aspect-square overflow-hidden rounded-lg border border-border bg-muted"
                                    >
                                        <AttachmentImage
                                            attachmentId={photo.id}
                                            alt={photo.name}
                                            loading="lazy"
                                            className="h-full w-full object-cover"
                                        />
                                        <span className="absolute inset-x-0 bottom-0 bg-black/55 px-1 py-0.5 text-center text-[10px] leading-tight text-white/90">
                                            {formatItemDate(photo.date)}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}

                        {documents.length > 0 && (
                            <div className="divide-y divide-border/60 rounded-lg border border-border/60">
                                {documents.map((doc) => (
                                    <button
                                        key={doc.id}
                                        type="button"
                                        onClick={() => onOpenDocument(doc)}
                                        className="flex w-full items-center gap-2 px-3 py-2.5 text-left hover:bg-muted/50"
                                    >
                                        <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                                        <span className="min-w-0 flex-1 truncate text-sm">{doc.name}</span>
                                        <span className="shrink-0 text-xs text-muted-foreground">
                                            {formatItemDate(doc.date)}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}

                        {nextCursor && (
                            <Button
                                variant="outline"
                                size="sm"
                                className="w-full"
                                disabled={loadingMore}
                                onClick={() => void loadMore()}
                            >
                                {loadingMore ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
                                Show more ({group.totalCount - items.length} remaining)
                            </Button>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export function SiteHistoryTab({ jobId }: { jobId: string }) {
    const historyQ = useQuery({
        queryKey: ["field", "site-history", jobId],
        queryFn: () => api.get<SiteHistoryResponse>(`/api/field/jobs/${jobId}/site-history`),
        enabled: !!jobId,
        staleTime: 60_000,
    });

    const [lightbox, setLightbox] = useState<{
        photos: { src: string; caption: string | null }[];
        index: number;
    } | null>(null);
    const [previewDoc, setPreviewDoc] = useState<{ id: string; fileName: string } | null>(null);

    if (historyQ.isLoading) {
        return (
            <div className="flex items-center justify-center py-16">
                <Spinner className="h-6 w-6" />
            </div>
        );
    }

    if (historyQ.isError) {
        return (
            <Card className="bg-card">
                <CardContent className="space-y-3 p-6 text-center">
                    <p className="text-sm text-muted-foreground">Couldn't load site history.</p>
                    <Button variant="outline" size="sm" onClick={() => void historyQ.refetch()}>
                        Retry
                    </Button>
                </CardContent>
            </Card>
        );
    }

    const groups = historyQ.data?.groups ?? [];
    if (!groups.length) {
        return (
            <Card className="bg-card">
                <CardContent className="p-6 text-center text-sm text-muted-foreground">
                    No history on file for this site yet.
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-3">
            {groups.map((group, i) => (
                <HistoryGroup
                    key={group.assetId ?? "site"}
                    jobId={jobId}
                    group={group}
                    defaultOpen={i === 0}
                    onOpenPhoto={(photos, index, groupLabel) =>
                        setLightbox({
                            photos: photos.map((p) => ({
                                src: attachmentDownloadUrl(p.id, { disposition: "inline" }),
                                caption: [formatItemDate(p.date), groupLabel].filter(Boolean).join(" · "),
                            })),
                            index,
                        })
                    }
                    onOpenDocument={(doc) => setPreviewDoc({ id: doc.id, fileName: doc.name })}
                />
            ))}

            <PhotoLightbox
                open={lightbox != null}
                onClose={() => setLightbox(null)}
                photos={lightbox?.photos ?? []}
                index={lightbox?.index ?? 0}
            />

            <AttachmentPreviewDialog
                open={previewDoc != null}
                onClose={() => setPreviewDoc(null)}
                attachmentId={previewDoc?.id ?? null}
                fileName={previewDoc?.fileName}
            />
        </div>
    );
}
